// Setup › Teams (`/c/{slug}/setup/teams`; spec 06 §Main screens 14, 04 §Org `teams`, M1-04): the
// company's teams as a tree, "New team" (standalone, or under a parent team), and a drawer per
// team (`?team=`) to rename it, move it, set its lead and human owner seats, add and remove its
// member seats, and delete it; TEAM-KPI (founder-approved 2026-10-21): the drawer's "KPIs"
// section is the team's scorecard for the focused week (its lead and managers edit it there). Composition (human / mixed / digital) is the server's, derived from
// the members' holders. The rules are the API's and are explained where they bite: a sub-team's
// members must be in its parent; a team of digital employees needs a human owner seat first; a
// team with sub-teams cannot be deleted. The team-shape rule (a manager's branch, or peers under
// one manager) is advice only. On load: teams and seats (2 calls); the drawer adds the team.

import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  ListRow,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  NetworkIcon,
  PencilIcon,
  PlusIcon,
  UsersIcon,
  XIcon,
} from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { Seat } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { TeamScorecardCard } from "../kpi/team-scorecard";
import { useReachConfirm } from "../memory/reach-confirm";
import { seatsQuery } from "../queries";
import { Loading, usePeriod } from "../screen-parts";
import { SetupDescription } from "../settings/setup-description";
import { ConfirmButton, ErrorNotice, FormError, MoreRows } from "../structure/parts";
import { useEveryPage } from "../use-every-page";
import { useFeature } from "../use-feature";
import {
  type ChartTeam,
  COMPOSITION_LABELS,
  type TeamTreeNode,
  teamShapeAdvice,
  teamTree,
} from "./chart-model";
import { ChartBody, ChartFrame, setupCrumb } from "./chart-parts";
import { chartKeys, teamQuery, teamsQuery } from "./chart-queries";

const TEAM_TONE = { human: "neutral", mixed: "info", digital: "accent" } as const;
const INDENT = ["pl-3", "pl-9", "pl-15", "pl-21", "pl-27"] as const;

export function TeamsScreen({
  api,
  company,
  teamId,
  onTeam,
}: {
  api: Api;
  company: CompanyAccess;
  /** The team open in the drawer (`?team=`), if any. */
  teamId: string | undefined;
  onTeam: (id: string | undefined) => void;
}) {
  const teams = useInfiniteQuery(teamsQuery(api, company.id));
  const seats = useInfiniteQuery(seatsQuery(api, company.id));
  // WAVE12-FIX: the member picker offers every seat, not only the first page's 50.
  useEveryPage(seats);
  const [creating, setCreating] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const all = teams.data?.pages.flatMap((p) => p.data) ?? [];
  const seatList = seats.data?.pages.flatMap((p) => p.data) ?? [];
  const titleOf = (id: string | null) =>
    id ? (seatList.find((s) => s.id === id)?.title ?? "A seat") : null;
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // 36 px rows (Chart UX pass 2026-10-21): the name opens the drawer; lead, owner and sub-team
  // count as meta; composition as a chip; Edit and Show in chart on hover.
  const row = (node: TeamTreeNode<ChartTeam>, depth: number) => {
    const t = node.team;
    const open = !collapsed.has(t.id);
    return (
      <li key={t.id} data-team={t.id} className="min-w-0">
        <ListRow
          lead={
            node.children.length ? (
              <button
                type="button"
                aria-expanded={open}
                aria-label={`${open ? "Collapse" : "Expand"} ${t.name}`}
                className="inline-flex size-4 items-center justify-center rounded-xs hover:bg-active hover:text-foreground"
                onClick={() => toggle(t.id)}
              >
                {open ? (
                  <ChevronDownIcon aria-hidden="true" />
                ) : (
                  <ChevronRightIcon aria-hidden="true" />
                )}
              </button>
            ) : (
              <UsersIcon aria-hidden="true" />
            )
          }
          title={t.name}
          onSelect={() => onTeam(t.id)}
          selected={t.id === teamId}
          className={INDENT[Math.min(depth, INDENT.length - 1)]}
          chips={
            <Badge variant={TEAM_TONE[t.composition]} data-composition={t.composition}>
              {COMPOSITION_LABELS[t.composition]}
            </Badge>
          }
          meta={[
            t.lead_seat_id ? `Lead: ${titleOf(t.lead_seat_id)}` : "No lead",
            t.human_owner_seat_id ? `Owner: ${titleOf(t.human_owner_seat_id)}` : null,
            node.children.length
              ? `${node.children.length} sub-team${node.children.length === 1 ? "" : "s"}`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          actions={
            <>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Edit ${t.name}`}
                title="Edit team"
                onClick={() => onTeam(t.id)}
              >
                <PencilIcon aria-hidden="true" />
              </Button>
              <Link
                to="/c/$slug/kpis/chart"
                params={{ slug: company.slug }}
                search={(prev: Record<string, unknown>) => ({
                  ...keepFocus(prev),
                  scope: `team:${t.id}`,
                })}
                aria-label={`Show ${t.name} in the chart`}
                title="Show in chart"
                className={cn(buttonVariants({ variant: "ghost", size: "icon-xs" }))}
              >
                <NetworkIcon aria-hidden="true" />
              </Link>
            </>
          }
        />
        {node.children.length && open ? (
          <ul className="flex min-w-0 flex-col">{node.children.map((c) => row(c, depth + 1))}</ul>
        ) : null}
      </li>
    );
  };

  return (
    <ChartFrame data-screen="setup-teams">
      <LocationHeader
        crumbs={[setupCrumb(company), { label: "People & access" }]}
        icon={UsersIcon}
        title="Teams"
        badge={<SetupDescription />}
        {...(teams.data ? { count: all.length } : {})}
        actions={
          // One "New team" at a time: with no teams, the empty state carries it.
          teams.isSuccess && !all.length ? undefined : (
            <Button size="sm" onClick={() => setCreating(true)} disabled={!seats.data}>
              <PlusIcon data-icon="inline-start" />
              New team
            </Button>
          )
        }
      />
      {teams.isPending ? (
        <ChartBody>
          <Loading label="Loading teams" rows={4} />
        </ChartBody>
      ) : teams.isError ? (
        <ChartBody>
          <ErrorNotice what="Teams" error={teams.error} />
        </ChartBody>
      ) : !all.length ? (
        <ChartBody>
          <EmptyState
            variant="card"
            icon={UsersIcon}
            title="No teams yet"
            description="A team groups seats: a department, a pod, a desk of digital employees. Teams scope the chart."
            actions={
              <Button onClick={() => setCreating(true)} disabled={!seats.data}>
                <PlusIcon data-icon="inline-start" />
                New team
              </Button>
            }
          />
        </ChartBody>
      ) : (
        <div className="flex min-w-0 flex-col">
          <ul data-slot="teams" aria-label="Teams" className="flex min-w-0 flex-col">
            {teamTree(all).map((n) => row(n, 0))}
          </ul>
          <ChartBody className="flex-none py-3">
            <MoreRows what="teams" q={teams} />
          </ChartBody>
        </div>
      )}
      {creating ? (
        <TeamDialog
          api={api}
          company={company}
          teams={all}
          seats={seatList}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            onTeam(id);
          }}
        />
      ) : null}
      {teamId ? (
        <TeamDrawer
          key={teamId}
          api={api}
          company={company}
          id={teamId}
          teams={all}
          seats={seatList}
          onClose={() => onTeam(undefined)}
        />
      ) : null}
    </ChartFrame>
  );
}

const open = (seats: Seat[]) =>
  seats
    .filter((s) => s.status !== "closed" && s.status !== "proposed")
    .sort((a, b) => a.title.localeCompare(b.title));

function SeatSelect({
  id,
  value,
  seats,
  none,
  onChange,
}: {
  id: string;
  value: string;
  seats: Seat[];
  none: string;
  onChange: (v: string) => void;
}) {
  return (
    <NativeSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <NativeSelectOption value="">{none}</NativeSelectOption>
      {open(seats).map((s) => (
        <NativeSelectOption key={s.id} value={s.id}>
          {s.title}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  );
}

function useTeamsChanged(companyId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: chartKeys.teams(companyId) });
    void queryClient.invalidateQueries({ queryKey: chartKeys.orgChart(companyId) });
  };
}

function TeamDialog({
  api,
  company,
  teams,
  seats,
  onClose,
  onCreated,
}: {
  api: Api;
  company: CompanyAccess;
  teams: ChartTeam[];
  seats: Seat[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const id = useId();
  const changed = useTeamsChanged(company.id);
  const [name, setName] = useState("");
  const [parent, setParent] = useState("");
  const [lead, setLead] = useState("");
  // D8: an agent on the lead seat is first shown what it will read in memory after
  const { confirmReach, reachDialog } = useReachConfirm(api, company.id);
  const create = useMutation({
    mutationFn: async () => {
      if (
        !(await confirmReach({
          kind: "team",
          team_id: null,
          parent_team_id: parent || null,
          lead_seat_id: lead || null,
        }))
      )
        return null;
      return api.client.teams.create({
        params: { co: company.id },
        body: {
          name: name.trim(),
          parent_team_id: parent || null,
          lead_seat_id: lead || null,
        },
      });
    },
    onSuccess: (team) => {
      if (!team) return;
      changed();
      onCreated(team.id);
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[520px]">
        {reachDialog}
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>New team</DialogTitle>
            <DialogDescription>
              Add its seats after creating it. A sub-team's seats must be in its parent first.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <Input
              id={`${id}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-parent`}>Parent team</FieldLabel>
            <NativeSelect
              id={`${id}-parent`}
              value={parent}
              onChange={(e) => setParent(e.target.value)}
            >
              <NativeSelectOption value="">None (a standalone team)</NativeSelectOption>
              {[...teams]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((t) => (
                  <NativeSelectOption key={t.id} value={t.id}>
                    {t.name}
                  </NativeSelectOption>
                ))}
            </NativeSelect>
          </Field>
          <Field>
            <FieldLabel htmlFor={`${id}-lead`}>Lead seat</FieldLabel>
            <SeatSelect
              id={`${id}-lead`}
              value={lead}
              seats={seats}
              none="No lead"
              onChange={setLead}
            />
            <FieldDescription>The lead need not be a member.</FieldDescription>
          </Field>
          <FormError what="Creating the team" error={create.error} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || create.isPending}>
              {create.isPending ? "Creating…" : "Create team"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TeamDrawer({
  api,
  company,
  id,
  teams,
  seats,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  teams: ChartTeam[];
  seats: Seat[];
  onClose: () => void;
}) {
  const team = useQuery(teamQuery(api, company.id, id));
  const kpi = useFeature("kpi");
  const week = usePeriod();
  const changed = useTeamsChanged(company.id);
  const queryClient = useQueryClient();
  const refresh = () => {
    changed();
    void queryClient.invalidateQueries({ queryKey: chartKeys.team(company.id, id) });
  };
  const remove = useMutation({
    mutationFn: () => api.client.teams.delete({ params: { co: company.id, id } }),
    onSuccess: () => {
      changed();
      onClose();
    },
  });
  const t = team.data;
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <DrawerHeader
        closeLabel="Close team details"
        actions={
          t ? (
            <ConfirmButton
              label="Delete team"
              title={`Delete ${t.name}?`}
              description="Its seats stay where they are; only the grouping goes. A team with sub-teams cannot be deleted: move or delete them first."
              confirmLabel="Delete team"
              pending={remove.isPending}
              onConfirm={() => remove.mutateAsync()}
            />
          ) : null
        }
      >
        Setup › Teams
      </DrawerHeader>
      <DrawerBody data-slot="team-drawer">
        {team.isPending ? (
          <Loading label="Loading the team" rows={4} />
        ) : team.isError ? (
          <ErrorNotice what="The team" error={team.error} />
        ) : t ? (
          <>
            <DrawerTitle>{t.name}</DrawerTitle>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant={TEAM_TONE[t.composition]} data-composition={t.composition}>
                {COMPOSITION_LABELS[t.composition]}
              </Badge>
              <span>
                {t.members.length} seat{t.members.length === 1 ? "" : "s"}
              </span>
            </div>
            <FormError what="Deleting the team" error={remove.error} />
            <DrawerSection title="Details" className="mt-6">
              <TeamForm
                key={t.updated_at}
                api={api}
                company={company}
                team={t}
                teams={teams}
                seats={seats}
                onSaved={refresh}
              />
            </DrawerSection>
            <DrawerSection title="Member seats">
              <Members api={api} company={company} team={t} seats={seats} onChanged={refresh} />
            </DrawerSection>
            {kpi.enabled ? (
              <DrawerSection title="KPIs">
                <TeamScorecardCard
                  api={api}
                  company={company}
                  team={{ id: t.id, name: t.name }}
                  week={week}
                />
              </DrawerSection>
            ) : null}
          </>
        ) : null}
      </DrawerBody>
    </Drawer>
  );
}

type TeamDetail = OperationOutput<"teams.get">;

function TeamForm({
  api,
  company,
  team,
  teams,
  seats,
  onSaved,
}: {
  api: Api;
  company: CompanyAccess;
  team: TeamDetail;
  teams: ChartTeam[];
  seats: Seat[];
  onSaved: () => void;
}) {
  const id = useId();
  const [name, setName] = useState(team.name);
  const [parent, setParent] = useState(team.parent_team_id ?? "");
  const [lead, setLead] = useState(team.lead_seat_id ?? "");
  const [owner, setOwner] = useState(team.human_owner_seat_id ?? "");
  // D8: a new parent (department) or lead first shows the agents what they will read after
  const { confirmReach, reachDialog } = useReachConfirm(api, company.id);
  const save = useMutation({
    mutationFn: async () => {
      if (
        ((parent || null) !== team.parent_team_id || (lead || null) !== team.lead_seat_id) &&
        !(await confirmReach({
          kind: "team",
          team_id: team.id,
          parent_team_id: parent || null,
          lead_seat_id: lead || null,
        }))
      )
        return null;
      return api.client.teams.update({
        params: { co: company.id, id: team.id },
        body: {
          name: name.trim(),
          parent_team_id: parent || null,
          lead_seat_id: lead || null,
          human_owner_seat_id: owner || null,
        },
      });
    },
    onSuccess: (r) => {
      if (r) onSaved();
    },
  });
  const dirty =
    name.trim() !== team.name ||
    (parent || null) !== team.parent_team_id ||
    (lead || null) !== team.lead_seat_id ||
    (owner || null) !== team.human_owner_seat_id;
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (dirty && name.trim()) save.mutate();
      }}
    >
      {reachDialog}
      <Field>
        <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
        <Input id={`${id}-name`} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-parent`}>Parent team</FieldLabel>
        <NativeSelect
          id={`${id}-parent`}
          value={parent}
          onChange={(e) => setParent(e.target.value)}
        >
          <NativeSelectOption value="">None (a standalone team)</NativeSelectOption>
          {teams
            .filter((x) => x.id !== team.id)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((x) => (
              <NativeSelectOption key={x.id} value={x.id}>
                {x.name}
              </NativeSelectOption>
            ))}
        </NativeSelect>
        <FieldDescription>Its member seats must all be in the parent team.</FieldDescription>
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-lead`}>Lead seat</FieldLabel>
        <SeatSelect
          id={`${id}-lead`}
          value={lead}
          seats={seats}
          none="No lead"
          onChange={setLead}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${id}-owner`}>Human owner seat</FieldLabel>
        <SeatSelect
          id={`${id}-owner`}
          value={owner}
          seats={seats}
          none="No owner"
          onChange={setOwner}
        />
        <FieldDescription>
          Required before a team is made only of digital employees: a person answers for it.
        </FieldDescription>
      </Field>
      <FormError what="Saving the team" error={save.error} />
      <div>
        <Button type="submit" disabled={!dirty || !name.trim() || save.isPending}>
          {save.isPending ? "Saving…" : "Save team"}
        </Button>
      </div>
    </form>
  );
}

function Members({
  api,
  company,
  team,
  seats,
  onChanged,
}: {
  api: Api;
  company: CompanyAccess;
  team: TeamDetail;
  seats: Seat[];
  onChanged: () => void;
}) {
  const id = useId();
  const [pick, setPick] = useState("");
  const memberIds = team.members.map((m) => m.seat_id);
  const titleOf = (sid: string) => seats.find((s) => s.id === sid)?.title ?? "A seat";
  // D8: an agent on the seat is first shown what it will read in memory after
  const { confirmReach, reachDialog } = useReachConfirm(api, company.id);
  const add = useMutation({
    mutationFn: async (seat: string) => {
      if (
        !(await confirmReach({
          kind: "seat_team",
          seat_id: seat,
          add_team_id: team.id,
          remove_team_id: null,
        }))
      )
        return null;
      return api.client.teams.addMember({
        params: { co: company.id, id: team.id },
        body: { seat_id: seat },
      });
    },
    onSuccess: () => {
      setPick("");
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: async (seat: string) => {
      if (
        !(await confirmReach({
          kind: "seat_team",
          seat_id: seat,
          add_team_id: null,
          remove_team_id: team.id,
        }))
      )
        return null;
      return api.client.teams.removeMember({
        params: { co: company.id, id: team.id, seat_id: seat },
      });
    },
    onSuccess: onChanged,
  });
  const advice = teamShapeAdvice(
    team,
    memberIds,
    new Map(seats.map((s) => [s.id, s.supervisor_seat_id])),
  );
  return (
    <div className="flex min-w-0 flex-col gap-3">
      {reachDialog}
      {team.members.length ? (
        <ul className="flex flex-col gap-1.5" data-slot="team-members">
          {team.members.map((m) => (
            <li
              key={m.seat_id}
              data-member={m.seat_id}
              className="flex min-w-0 items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5"
            >
              <span className="min-w-0 flex-1 truncate text-sm">{titleOf(m.seat_id)}</span>
              {m.seat_id === team.lead_seat_id ? <Badge variant="accent">Lead</Badge> : null}
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Remove ${titleOf(m.seat_id)}`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(m.seat_id)}
              >
                <XIcon aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No seats yet.</p>
      )}
      {advice ? (
        <Notice tone="warning" title="Team shape (advice, not a rule)" data-slot="team-advice">
          {advice}
        </Notice>
      ) : null}
      <div className="flex min-w-0 items-end gap-2">
        <Field className="min-w-0 flex-1">
          <FieldLabel htmlFor={`${id}-seat`}>Add a seat</FieldLabel>
          <NativeSelect id={`${id}-seat`} value={pick} onChange={(e) => setPick(e.target.value)}>
            <NativeSelectOption value="">Choose a seat…</NativeSelectOption>
            {open(seats)
              .filter((s) => !memberIds.includes(s.id))
              .map((s) => (
                <NativeSelectOption key={s.id} value={s.id}>
                  {s.title}
                </NativeSelectOption>
              ))}
          </NativeSelect>
        </Field>
        <Button
          variant="secondary"
          disabled={!pick || add.isPending}
          onClick={() => add.mutate(pick)}
        >
          <PlusIcon aria-hidden="true" />
          Add
        </Button>
      </div>
      <FormError what="Adding the seat" error={add.error} />
      <FormError what="Removing the seat" error={remove.error} />
      <Link
        to="/c/$slug/kpis/chart"
        params={{ slug: company.slug }}
        search={(prev: Record<string, unknown>) => ({
          ...keepFocus(prev),
          scope: `team:${team.id}`,
        })}
        className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary-text underline-offset-4 hover:underline"
      >
        <NetworkIcon aria-hidden="true" className="size-4" />
        Show the team in the chart
      </Link>
    </div>
  );
}
